import { useMemo } from 'react';

import { Box, Heading, List, ListItem, Tab, TabList, TabPanel, TabPanels, Tabs, Text } from '@chakra-ui/react';
import { ArrowRightIcon } from '@udacity/chakra-uds-icons';

import { ButtonLink } from '~/components/button-link';
import { Card } from '~/components/card';
import { ContentContainer } from '~/components/container';
import { Markdown } from '~/components/markdown';
import { Space, spaceHeight } from '~/components/space';
import { trackEvent } from '~/features/analytics/track-event';
import { ProgramCatalogCard } from '~/features/catalog/components/catalog-card-program';
import { CatalogItem } from '~/features/catalog/models/catalog-item';
import { useCourseLink } from '~/features/program/hooks/use-course-link';
import { useI18n } from '~/hooks/use-i18n';

import {
  LearningPathProgram,
  LearningPathProgramContentMap,
} from '../controllers/get-learning-paths-program-content-map';

type LearningPath = {
  description: string | null;
  title: string | null;
  marketingTitle: string | null;
  programs: Array<{
    key: string;
  }> | null;
};

type LearningPathsSectionProps = {
  learningPaths: LearningPath[];
  learningPathProgramContentMap: LearningPathProgramContentMap;
};

function programClickHandler(program: CatalogItem) {
  trackEvent({ type: 'click', name: 'Schools Page CTA Clicked', label: program.title! });
}

export function LearningPathsSection({ learningPaths, learningPathProgramContentMap }: LearningPathsSectionProps) {
  const { isRtl } = useI18n();
  const panels = useMemo(() => {
    return learningPaths?.map((learningPath) => (
      <LearningPathPanel
        key={learningPath.title}
        learningPath={learningPath}
        learningPathProgramContentMap={learningPathProgramContentMap}
      />
    ));
  }, [learningPaths, learningPathProgramContentMap]);

  return (
    <Box
      as='section'
      backgroundColor='white'
      borderBottom='1px solid'
      borderBottomColor='gray.400'
      paddingY={spaceHeight}
    >
      <ContentContainer>
        <Heading as='h2' size='h2'>
          Learning Paths by Job Title
        </Heading>
        <Box h='16px' />
      </ContentContainer>
      <Tabs>
        <Box
          maxWidth='78.125rem'
          overflowX='auto'
          overflowY='hidden'
          marginLeft={
            isRtl
              ? undefined
              : {
                  xl: 'calc((100vw - 78.125rem)/2)',
                }
          }
          marginRight={
            isRtl
              ? {
                  xl: 'calc((100vw - 78.125rem)/2)',
                }
              : undefined
          }
        >
          <TabList marginX={{ base: '16px', md: 'unset' }}>
            {learningPaths.map((learningPath) => {
              if (!learningPath) return null;
              return (
                <Tab
                  key={learningPath.title}
                  whiteSpace='nowrap'
                  onClick={() =>
                    trackEvent({
                      type: 'click',
                      name: 'Schools Page CTA Clicked',
                      label: learningPath.title!,
                    })
                  }
                >
                  {learningPath.title}
                </Tab>
              );
            })}
          </TabList>
        </Box>
        <ContentContainer>
          <TabPanels>{panels}</TabPanels>
        </ContentContainer>
      </Tabs>
    </Box>
  );
}

function LearningPathPanel({
  learningPath,
  learningPathProgramContentMap,
}: {
  learningPath: LearningPath;
  learningPathProgramContentMap: LearningPathProgramContentMap;
}) {
  const programKeys = learningPath?.programs?.map((program) => program.key);
  return (
    <TabPanel key={learningPath.title} padding='24px 0px 0px'>
      {learningPath.description && (
        <Box maxWidth={{ md: '640px' }}>
          <Markdown markdownText={learningPath.description} />
        </Box>
      )}
      <Space />
      {learningPath.marketingTitle && (
        <Heading as='p' size='h4' textTransform='capitalize'>
          {learningPath.marketingTitle}
        </Heading>
      )}
      <Box h='8px' />
      <List
        display='flex'
        flexWrap='wrap'
        gap='24px 32px'
        flexDirection={{
          base: 'column',
          lg: 'row',
        }}
      >
        {programKeys?.map((programKey, index) => (
          <LearningPathCard
            key={programKey}
            index={index}
            learningPathProgram={learningPathProgramContentMap[programKey]}
            programKey={programKey}
          />
        ))}
      </List>
    </TabPanel>
  );
}

function LearningPathCard({
  programKey,
  learningPathProgram: learningPathContent,
  index,
}: {
  programKey: string;
  learningPathProgram: LearningPathProgram;
  index: number;
}) {
  const courseLink = useCourseLink({ courseSlug: learningPathContent.catalogItem?.slug ?? '' });
  const skills = learningPathContent.skills || [];
  const hasSkills = skills.length > 0;
  const programSummary = learningPathContent.summary || '';
  const catalogItem = learningPathContent.catalogItem;

  if (!catalogItem) return null;

  return (
    <ListItem
      width={{
        base: '100%',
        xl: '544px',
      }}
    >
      <Card
        backgroundColor='gray.100'
        display='flex'
        gap='24px'
        padding='24px'
        width='100%'
        flexDir={{
          base: 'column-reverse',
          md: 'row',
        }}
      >
        {/* DESKTOP CATALOG CARD */}
        <Box
          display={{
            base: 'none',
            md: 'block',
          }}
        >
          <ProgramCatalogCard
            catalogItem={catalogItem}
            width={{
              md: '190px',
            }}
            onClick={() => programClickHandler(catalogItem)}
          />
        </Box>
        <Box>
          <Text size='caption'>Step {index + 1}</Text>
          {/* MOBILE CATALOG CARD */}
          <Box
            marginTop='24px'
            display={{
              base: 'block',
              md: 'none',
            }}
          >
            <ProgramCatalogCard
              catalogItem={catalogItem}
              width={{
                md: '190px',
              }}
              onClick={() => programClickHandler(catalogItem)}
            />
          </Box>
          <Box h='24px' />
          <Text fontWeight='600'>{hasSkills ? 'Skills Covered' : 'Program Description'}</Text>
          <Text noOfLines={7} textTransform={hasSkills ? 'capitalize' : 'unset'}>
            {hasSkills ? skills.join(', ') : programSummary}
          </Text>
          <Box h='12px' />
          <ButtonLink
            buttonProps={{
              display: 'inline-flex',
              variant: 'link',
              gap: '2px',
              onClick: () => programClickHandler(catalogItem),
            }}
            linkProps={{
              href: courseLink,
            }}
          >
            Learn More <ArrowRightIcon height='20px' width='20px' />
          </ButtonLink>
        </Box>
      </Card>
    </ListItem>
  );
}
